import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import FreshBooksPicker from "../components/FreshBooksPicker";
import OdooConnectDialog from "../components/OdooConnectDialog";
import { HelpTip } from "../components/HelpTip";
import { timeAgo } from "../lib/format";
import CompanyGroups from "../components/CompanyGroups";
import { isHidden } from "../lib/platforms";
import {
  Plug, RefreshCw, Clock3, CheckCircle2, XCircle, ExternalLink, AlertTriangle,
  ChevronDown, ChevronUp,
} from "lucide-react";

const ICONS = {
  quickbooks: (
    <div className="w-12 h-12 border border-neutral-900 flex items-center justify-center font-display font-bold text-lg">
      QB
    </div>
  ),
  xero: (
    <div className="w-12 h-12 border border-neutral-900 flex items-center justify-center font-display font-bold text-lg">
      XR
    </div>
  ),
  zoho: (
    <div className="w-12 h-12 border border-neutral-900 flex items-center justify-center font-display font-bold text-lg">
      ZB
    </div>
  ),
  bc: (
    <div className="w-12 h-12 border border-neutral-900 flex items-center justify-center font-display font-bold text-lg">
      BC
    </div>
  ),
  freshbooks: (
    <div className="w-12 h-12 border border-neutral-900 flex items-center justify-center font-display font-bold text-lg">
      FB
    </div>
  ),
  odoo: (
    <div className="w-12 h-12 border border-neutral-900 flex items-center justify-center font-display font-bold text-sm">
      ODOO
    </div>
  ),
  netsuite: (
    <div className="w-12 h-12 border border-neutral-900 flex items-center justify-center font-display font-bold text-lg">
      NS
    </div>
  ),
};

export default function Connections() {
  const [connections, setConnections] = useState([]);
  const [receivingGl, setReceivingGl] = useState(null);
  const [running, setRunning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [banner, setBanner] = useState(null);
  const [bcCompanies, setBcCompanies] = useState(null); // {companies, selected_id, selected_name, error}
  const [bcPicking, setBcPicking] = useState(false);
  const [fbBiz, setFbBiz] = useState(null); // {businesses, selected_account_id, selected_name}
  const [fbPicking, setFbPicking] = useState(false);
  const [odooOpen, setOdooOpen] = useState(false);
  const [showOthers, setShowOthers] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [myCompanyId, setMyCompanyId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Show banner based on OAuth callback redirect
  useEffect(() => {
    const qbo = searchParams.get("qbo");
    const xero = searchParams.get("xero");
    const zoho = searchParams.get("zoho");
    if (qbo === "connected") {
      const realm = searchParams.get("realm");
      setBanner({
        kind: "success",
        text: `QuickBooks connected · realm ${realm}. Pulling real invoices now…`,
      });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    } else if (qbo === "error") {
      const reason = searchParams.get("reason") || "unknown";
      setBanner({ kind: "error", text: `QuickBooks connection failed: ${reason}` });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    } else if (xero === "connected") {
      const tenant = searchParams.get("tenant");
      setBanner({
        kind: "success",
        text: `Xero connected · tenant ${tenant}. Pulling real invoices now…`,
      });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    } else if (xero === "error") {
      const reason = searchParams.get("reason") || "unknown";
      setBanner({ kind: "error", text: `Xero connection failed: ${reason}` });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    } else if (zoho === "connected") {
      const org = searchParams.get("org");
      setBanner({
        kind: "success",
        text: `Zoho Books connected${org ? ` · org ${org}` : ""}. Pulling real invoices now…`,
      });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    } else if (zoho === "error") {
      const reason = searchParams.get("reason") || "unknown";
      setBanner({ kind: "error", text: `Zoho Books connection failed: ${reason}` });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    } else if (searchParams.get("freshbooks")) {
      const fb = searchParams.get("freshbooks");
      const org = searchParams.get("org");
      setBanner(fb === "connected"
        ? { kind: "success", text: `FreshBooks connected${org ? ` · ${org}` : ""}. Pulling real invoices now…` }
        : fb === "pick_business"
          ? { kind: "success", text: "FreshBooks connected. Pick which business to use on the FreshBooks card." }
          : { kind: "error", text: `FreshBooks connection failed: ${searchParams.get("reason") || "unknown"}` });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    } else if (searchParams.get("bc") === "connected") {
      const org = searchParams.get("org");
      setBanner({
        kind: "success",
        text: `Business Central connected${org ? ` · ${org}` : ""}. Pulling real invoices now…`,
      });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    } else if (searchParams.get("bc") === "error") {
      const reason = searchParams.get("reason") || "unknown";
      setBanner({ kind: "error", text: `Business Central connection failed: ${reason}` });
      setTimeout(() => setSearchParams({}, { replace: true }), 100);
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    const [{ data: conns }, { data: me }] = await Promise.all([
      api.get("/connections"),
      api.get("/companies/me").catch(() => ({ data: null })),
    ]);
    setConnections(conns);
    setIsSuperAdmin(Boolean(me && me.is_super_admin));
    setMyCompanyId((me && me.company && me.company.id) || null);
    setReceivingGl(((me && me.company && me.company.receiving_gl) || "quickbooks").toLowerCase());
    const fb = (conns || []).find((c) => c.erp === "freshbooks" && c.connected);
    if (fb) {
      api.get("/freshbooks/businesses").then(({ data }) => setFbBiz(data)).catch(() => setFbBiz(null));
    } else {
      setFbBiz(null);
    }
    const bc = (conns || []).find((c) => c.erp === "bc" && c.connected);
    if (bc) {
      try {
        const { data } = await api.get("/bc/companies");
        setBcCompanies(data);
      } catch (e) {
        setBcCompanies({ companies: [],
                         error: e?.response?.data?.detail || "BC companies lookup failed" });
      }
    } else {
      setBcCompanies(null);
    }
  }, []);

  const selectBcCompany = async (id, name) => {
    setBcPicking(true);
    try {
      await api.post("/bc/select-company", { id, name });
      setBanner({ kind: "success",
                  text: `Business Central company set to ${name || id}. Pulling now…` });
      await load();
    } catch (e) {
      setBanner({ kind: "error",
                  text: e?.response?.data?.detail || "Could not set BC company" });
    } finally {
      setBcPicking(false);
    }
  };

  const setReceivingGL = async (erp) => {
    try {
      await api.put("/companies/me/receiving-gl", { receiving_gl: erp });
      setReceivingGl(erp);
      setBanner({ kind: "success",
                  text: `Receiving GL set to ${erp}. New bills will post there; already-synced invoices keep their original target.` });
      await load();
    } catch (e) {
      setBanner({ kind: "error",
                  text: e?.response?.data?.detail || `Could not set Receiving GL to ${erp}` });
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const toggleMock = async (id, current) => {
    await api.patch(`/connections/${id}`, { connected: !current });
    await load();
  };

  const connectQuickBooks = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/qbo/auth-url");
      window.location.href = data.authorization_url;
    } catch (e) {
      setBanner({ kind: "error", text: "Could not start QuickBooks OAuth — check backend logs" });
      setConnecting(false);
    }
  };

  const disconnectQuickBooks = async () => {
    if (!window.confirm("Disconnect QuickBooks? Tokens will be revoked and sync will stop.")) return;
    await api.post("/qbo/disconnect");
    await load();
    setBanner({ kind: "success", text: "QuickBooks disconnected." });
  };

  const connectXero = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/xero/connect");
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else if (data.tenant_id) {
        setBanner({ kind: "success",
                    text: `Xero connected · tenant ${data.tenant_id}. Pulling real invoices now…` });
        await load();
        setConnecting(false);
      } else {
        setConnecting(false);
      }
    } catch (e) {
      setBanner({ kind: "error",
                  text: e?.response?.data?.detail || "Could not start Xero connect — check backend logs" });
      setConnecting(false);
    }
  };

  const disconnectXero = async () => {
    if (!window.confirm("Disconnect Xero? Tokens will be revoked and sync will stop.")) return;
    await api.post("/xero/disconnect");
    await load();
    setBanner({ kind: "success", text: "Xero disconnected." });
  };

  const connectZoho = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/zoho/connect");
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setConnecting(false);
      }
    } catch (e) {
      setBanner({ kind: "error",
                  text: e?.response?.data?.detail || "Could not start Zoho connect — check backend logs" });
      setConnecting(false);
    }
  };

  const disconnectZoho = async () => {
    if (!window.confirm("Disconnect Zoho Books? Tokens will be revoked and sync will stop.")) return;
    await api.post("/zoho/disconnect");
    await load();
    setBanner({ kind: "success", text: "Zoho Books disconnected." });
  };

  const connectBc = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/bc/connect");
      if (data.authorization_url) window.location.href = data.authorization_url;
      else setConnecting(false);
    } catch (e) {
      setBanner({ kind: "error",
                  text: e?.response?.data?.detail || "Could not start Business Central connect" });
      setConnecting(false);
    }
  };

  const disconnectBc = async () => {
    if (!window.confirm("Disconnect Business Central? Tokens will be revoked and sync will stop.")) return;
    await api.post("/bc/disconnect");
    await load();
    setBanner({ kind: "success", text: "Business Central disconnected." });
  };

  const connectFb = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/freshbooks/connect");
      if (data.authorization_url) window.location.href = data.authorization_url;
      else setConnecting(false);
    } catch (e) {
      setBanner({ kind: "error",
                  text: e?.response?.data?.detail || "Could not start FreshBooks connect" });
      setConnecting(false);
    }
  };

  const disconnectFb = async () => {
    if (!window.confirm("Disconnect FreshBooks? Tokens will be revoked and sync will stop.")) return;
    await api.post("/freshbooks/disconnect");
    await load();
    setBanner({ kind: "success", text: "FreshBooks disconnected." });
  };

  const selectFbBusiness = async (accountId) => {
    setFbPicking(true);
    try {
      const { data } = await api.post("/freshbooks/business", { account_id: accountId });
      setBanner({ kind: "success", text: `FreshBooks business set · ${data.name}. Pulling invoices now…` });
      await load();
    } catch (e) {
      setBanner({ kind: "error", text: e?.response?.data?.detail || "Could not set FreshBooks business" });
    } finally {
      setFbPicking(false);
    }
  };

  const onOdooConnected = async (data) => {
    setBanner({ kind: "success",
                text: `Odoo connected · ${data.company_name || data.account_id}. Pulling invoices now…` });
    await load();
  };

  const disconnectOdoo = async () => {
    if (!window.confirm("Disconnect Odoo? The stored API key will be deleted and sync will stop.")) return;
    await api.post("/odoo/disconnect");
    await load();
    setBanner({ kind: "success", text: "Odoo disconnected." });
  };

  const runAll = async () => {
    setRunning(true);
    try {
      await api.post("/sync/run");
      await load();
    } finally {
      setRunning(false);
    }
  };

  const shown = connections.filter((c) => isSuperAdmin || c.connected || !isHidden(c.erp));
  const primaryConns = shown.filter((c) => c.visible_by_default !== false);
  const otherConns = shown.filter((c) => c.visible_by_default === false);
  const renderCard = (c) => {
    const ro = isSuperAdmin && c.company_id !== myCompanyId;
    return (
    <ConnectionCard
      key={c.id}
      c={c}
      connecting={connecting}
      receivingGl={receivingGl}
      onSetReceivingGL={setReceivingGL}
      onConnectQbo={connectQuickBooks}
      onDisconnectQbo={disconnectQuickBooks}
      onConnectXero={connectXero}
      onDisconnectXero={disconnectXero}
      onConnectZoho={connectZoho}
      onDisconnectZoho={disconnectZoho}
      onConnectBc={connectBc}
      onDisconnectBc={disconnectBc}
      onConnectFb={connectFb}
      onDisconnectFb={disconnectFb}
      fbBiz={c.erp === "freshbooks" && !ro ? fbBiz : null}
      fbPicking={fbPicking}
      onSelectFbBusiness={selectFbBusiness}
      onConnectOdoo={() => setOdooOpen(true)}
      onDisconnectOdoo={disconnectOdoo}
      onToggleMock={toggleMock}
      bcCompanies={c.erp === "bc" && !ro ? bcCompanies : null}
      bcPicking={bcPicking}
      onSelectBcCompany={selectBcCompany}
      readOnly={ro}
    />
    );
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="endpoints"
        title="ERP Connections"
        description="Connect the accounting system you use. You'll sign in on the provider's own page, and LF never sees your password."
        testid="connections-header"
        help="connect-your-accounting-system"
        actions={
          <button
            type="button"
            className="btn-primary flex items-center gap-2"
            onClick={runAll}
            disabled={running}
            data-testid="connections-run-sync"
          >
            <RefreshCw size={14} className={running ? "pulse" : ""} />
            {running ? "running…" : "trigger sync"}
          </button>
        }
      />

      <div className="p-8 space-y-6">
        {banner && (
          <div
            className={`border px-4 py-3 flex items-start justify-between gap-4 ${
              banner.kind === "success"
                ? "border-green-600 text-green-700"
                : "border-red-600 text-red-700"
            }`}
            data-testid="connection-banner"
          >
            <div className="flex items-start gap-2 text-sm">
              {banner.kind === "success" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              <span>{banner.text}</span>
            </div>
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="text-xs uppercase tracking-widest"
              data-testid="banner-dismiss"
            >
              dismiss
            </button>
          </div>
        )}

        {isSuperAdmin ? (
          <CompanyGroups connections={connections} renderItem={renderCard}
                         testid="admin-connections-groups" />
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="primary-connections">
          {primaryConns.map(renderCard)}
        </div>
        )}

        {!isSuperAdmin && otherConns.length > 0 && (
          <div data-testid="other-connections-section">
            <button type="button" onClick={() => setShowOthers((v) => !v)}
                    className="text-sm underline underline-offset-4 text-neutral-700 hover:text-neutral-900 flex items-center gap-1"
                    data-testid="add-another-gl-toggle" aria-expanded={showOthers}>
              {showOthers ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showOthers ? "Hide other accounting systems" : "Add another accounting system"}
            </button>
            {showOthers && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4" data-testid="other-connections">
                {otherConns.map(renderCard)}
              </div>
            )}
          </div>
        )}

        <div className="card-flat p-6" data-testid="field-mapping-card">
          <div className="label-mini">field mapping</div>
          <h3 className="font-display text-xl font-semibold mt-1">10 fields propagated bidirectionally</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 text-xs">
            {[
              "invoice_number",
              "customer_name",
              "customer_email",
              "issue_date",
              "due_date",
              "total_amount",
              "tax_amount",
              "currency",
              "status",
              "description",
            ].map((f) => (
              <div key={f} className="border border-neutral-200 px-2 py-1.5 font-mono">{f}</div>
            ))}
          </div>
        </div>
      </div>
      <OdooConnectDialog open={odooOpen} onOpenChange={setOdooOpen} onConnected={onOdooConnected} />
    </Layout>
  );
}

function ConnectionCard({ c, connecting, receivingGl, onSetReceivingGL,
                         onConnectQbo, onDisconnectQbo,
                         onConnectXero, onDisconnectXero,
                         onConnectZoho, onDisconnectZoho,
                         onConnectBc, onDisconnectBc, onToggleMock,
                         bcCompanies, bcPicking, onSelectBcCompany,
                         onConnectFb, onDisconnectFb, fbBiz, fbPicking, onSelectFbBusiness,
                         onConnectOdoo, onDisconnectOdoo, readOnly = false }) {
  const isQbo = c.erp === "quickbooks";
  const isXero = c.erp === "xero";
  const isZoho = c.erp === "zoho";
  const isBc = c.erp === "bc";
  const isFb = c.erp === "freshbooks";
  const isOauth = isQbo || isXero || isZoho || isBc || isFb;
  const fbNeedsPick = isFb && c.connected && !c.account_id;
  const fbReconnect = isFb && c.status === "reconnect_needed";
  const isOdoo = c.erp === "odoo";
  const isLiveGL = isOauth || isOdoo; // netsuite stub is excluded from Receiving GL
  const isReceivingGL = (receivingGl || "").toLowerCase() === c.erp;

  // BC picker state
  const bcErr = isBc && bcCompanies ? bcCompanies.error : null;
  const bcSelectedId = isBc && bcCompanies ? bcCompanies.selected_id : null;
  const bcSelectedName = isBc && bcCompanies ? bcCompanies.selected_name : null;
  const bcList = isBc && bcCompanies ? (bcCompanies.companies || []) : [];
  const bcNeedsPick = isBc && c.connected && !bcSelectedId && !bcErr;
  const [chosen, setChosen] = useState("");

  // Resolve the account label with a clear precedence:
  // 1. a real BC error → show it, 2. the selected BC company name,
  // 3. the stored account_id, 4. "no account connected".
  let acctLabel = c.account_id;
  let acctItalic = false;
  if (isBc) {
    if (bcErr) {
      acctLabel = `error · ${bcErr}`;
      acctItalic = true;
    } else if (bcSelectedName) {
      acctLabel = bcSelectedName;
    } else if (bcNeedsPick) {
      acctLabel = "pick a company below";
      acctItalic = true;
    }
  }

  if (isOdoo && c.connected && c.account_id) {
    acctLabel = c.account_label ? `${c.account_id} · ${c.account_label}` : c.account_id;
  }
  if (isFb) {
    if (fbReconnect) {
      acctLabel = "reconnect needed";
      acctItalic = true;
    } else if (fbNeedsPick) {
      acctLabel = "pick a business below";
      acctItalic = true;
    } else if (c.account_label) {
      acctLabel = c.account_label;
    }
  }

  return (
    <div className="card-flat p-6" data-testid={`connection-${c.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {ICONS[c.erp] || <Plug size={20} />}
          <div>
            <div className="font-display text-xl font-semibold">{c.name}</div>
            <div className="text-xs text-neutral-500 mt-1" data-testid={`account-label-${c.erp}`}>
              {acctLabel ? (
                acctItalic ? (
                  <span className="italic text-red-600">{acctLabel}</span>
                ) : (
                  <>account id · <span className="font-mono text-neutral-900">{acctLabel}</span></>
                )
              ) : (
                <span className="italic">no account connected</span>
              )}
            </div>
            <div className="label-mini mt-2">
              {isQbo ? `real oauth 2.0 · ${c.environment || "sandbox"}`
                : isXero ? "real oauth 2.0 · web app"
                : isZoho ? "real oauth 2.0 · server-based"
                : isBc ? "real oauth 2.0 · multitenant (entra id)"
                : isFb ? "real oauth 2.0 · rotating refresh tokens"
                : isOdoo ? "api key · json-rpc (encrypted at rest)"
                : "simulated connector"}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-2 text-xs">
            <span
              className="status-dot"
              style={{ background: c.connected ? "var(--success)" : "var(--error)" }}
            />
            <span className="uppercase tracking-widest">
              {fbReconnect ? "reconnect needed" : c.connected ? "live" : "offline"}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="border border-neutral-200 p-3">
          <div className="label-mini">last pulled</div>
          <div className="text-sm font-mono mt-1 flex items-center gap-2" data-testid={`last-pulled-${c.erp}`}>
            <Clock3 size={12} /> {timeAgo(c.last_pulled_at)}
          </div>
        </div>
        <div className="border border-neutral-200 p-3">
          <div className="label-mini">last pushed</div>
          <div className="text-sm font-mono mt-1 flex items-center gap-2" data-testid={`last-pushed-${c.erp}`}>
            <Clock3 size={12} /> {timeAgo(c.last_sync_at)}
          </div>
        </div>
        <div className="border border-neutral-200 p-3">
          <div className="label-mini">schedule</div>
          <div className="text-sm font-mono mt-1">every 5 min</div>
        </div>
      </div>

      {c.connected && c.last_pull_error && (
        <div className="mt-4 border border-amber-600 bg-amber-50 p-3 text-xs text-amber-900"
             data-testid={`pull-error-${c.erp}`}>
          <div className="label-mini text-amber-900">last pull error · {timeAgo(c.last_pull_error_at)}</div>
          <div className="mt-1 font-mono break-all">{c.last_pull_error}</div>
        </div>
      )}

      {readOnly ? (
        <div className="mt-4 label-mini" data-testid={`view-only-${c.id}`}>
          view only · managed by {c.company_name || "this company"}
        </div>
      ) : (<>
      {isBc && c.connected && bcErr && (
        <div className="mt-4 border border-red-600 bg-red-50 p-3 text-xs text-red-700"
             data-testid="bc-error-row">
          <div className="label-mini text-red-700">business central error</div>
          <div className="mt-1 font-mono break-all">{bcErr}</div>
        </div>
      )}

      {bcNeedsPick && bcList.length > 1 && (
        <div className="mt-4 border border-neutral-900 p-3" data-testid="bc-picker-row">
          <div className="label-mini flex items-center">
            pick a bc company
            <HelpTip text="Choose which Business Central company receives and sends invoices."
                     anchor="connect-business-central" label="About the Business Central company"
                     testid="help-bc-company" />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <select
              className="flex-1 border border-neutral-900 px-2 py-1.5 font-mono text-sm"
              value={chosen}
              onChange={(e) => setChosen(e.target.value)}
              data-testid="bc-company-select"
            >
              <option value="">— select —</option>
              {bcList.map((co) => (
                <option key={co.id} value={co.id}>{co.name}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={!chosen || bcPicking}
              onClick={() => {
                const co = bcList.find((x) => x.id === chosen);
                onSelectBcCompany && onSelectBcCompany(chosen, co && co.name);
              }}
              data-testid="bc-company-set"
            >
              {bcPicking ? "setting…" : "set company"}
            </button>
          </div>
        </div>
      )}

      {bcNeedsPick && bcList.length === 1 && (
        <div className="mt-4 border border-neutral-900 p-3 flex items-center gap-2"
             data-testid="bc-picker-row">
          <div className="label-mini flex-1">
            <HelpTip text="Choose which Business Central company receives and sends invoices."
                     anchor="connect-business-central" label="About the Business Central company"
                     testid="help-bc-company" />{" "}
            one bc company found · <span className="font-mono text-neutral-900">{bcList[0].name}</span>
          </div>
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={bcPicking}
            onClick={() => onSelectBcCompany && onSelectBcCompany(bcList[0].id, bcList[0].name)}
            data-testid="bc-company-set"
          >
            {bcPicking ? "setting…" : "use this company"}
          </button>
        </div>
      )}

      {fbNeedsPick && (fbBiz?.businesses || []).length > 0 && (
        <FreshBooksPicker businesses={fbBiz.businesses} picking={fbPicking}
                          onSelect={onSelectFbBusiness} />
      )}

      {isLiveGL && (isReceivingGL || c.can_switch_receiving_gl !== false) && (
        <div
          className={`mt-4 border p-3 flex items-center justify-between gap-3 ${
            isReceivingGL ? "border-neutral-900 bg-neutral-50" : "border-neutral-200"
          }`}
          data-testid={`receiving-gl-row-${c.erp}`}
        >
          <div className="flex-1">
            <div className="label-mini flex items-center">
              receiving gl
              <HelpTip text="Incoming bills go into this one system. Changing it only affects invoices that haven't synced yet."
                       anchor="receiving-gl" label="About the Receiving GL"
                       testid={`help-receiving-gl-${c.erp}`} />
            </div>
            <div className="text-sm mt-1">
              {isReceivingGL
                ? "new bills post into this GL"
                : c.connected
                  ? "click to receive bills into this GL"
                  : "connect this GL to make it selectable"}
            </div>
          </div>
          {isReceivingGL ? (
            <span
              className="text-[10px] uppercase tracking-widest border border-neutral-900 px-2 py-1 font-mono"
              data-testid={`receiving-gl-badge-${c.erp}`}
            >
              ✓ selected
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onSetReceivingGL && onSetReceivingGL(c.erp)}
              disabled={!c.connected}
              className="btn-outline text-xs"
              data-testid={`receiving-gl-select-${c.erp}`}
            >
              set as receiving gl
            </button>
          )}
        </div>
      )}

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        {isQbo ? (
          c.connected ? (
            <button type="button" className="btn-outline flex items-center gap-2"
                    onClick={onDisconnectQbo} data-testid={`disconnect-${c.id}`}>
              <XCircle size={12} /> disconnect
            </button>
          ) : (
            <button type="button" className="btn-primary flex items-center gap-2"
                    onClick={onConnectQbo} disabled={connecting}
                    data-testid="connect-quickbooks">
              <ExternalLink size={12} />
              {connecting ? "redirecting…" : "connect to quickbooks"}
            </button>
          )
        ) : isXero ? (
          c.connected ? (
            <button type="button" className="btn-outline flex items-center gap-2"
                    onClick={onDisconnectXero} data-testid={`disconnect-${c.id}`}>
              <XCircle size={12} /> disconnect
            </button>
          ) : (
            <button type="button" className="btn-primary flex items-center gap-2"
                    onClick={onConnectXero} disabled={connecting}
                    data-testid="connect-xero">
              <ExternalLink size={12} />
              {connecting ? "redirecting…" : "connect to xero"}
            </button>
          )
        ) : isZoho ? (
          c.connected ? (
            <button type="button" className="btn-outline flex items-center gap-2"
                    onClick={onDisconnectZoho} data-testid={`disconnect-${c.id}`}>
              <XCircle size={12} /> disconnect
            </button>
          ) : (
            <button type="button" className="btn-primary flex items-center gap-2"
                    onClick={onConnectZoho} disabled={connecting}
                    data-testid="connect-zoho">
              <ExternalLink size={12} />
              {connecting ? "redirecting…" : "connect to zoho books"}
            </button>
          )
        ) : isBc ? (
          c.connected ? (
            <button type="button" className="btn-outline flex items-center gap-2"
                    onClick={onDisconnectBc} data-testid={`disconnect-${c.id}`}>
              <XCircle size={12} /> disconnect
            </button>
          ) : (
            <button type="button" className="btn-primary flex items-center gap-2"
                    onClick={onConnectBc} disabled={connecting}
                    data-testid="connect-bc">
              <ExternalLink size={12} />
              {connecting ? "redirecting…" : "connect to business central"}
            </button>
          )
        ) : isFb ? (
          c.connected ? (
            <>
              {fbReconnect && (
                <button type="button" className="btn-primary flex items-center gap-2"
                        onClick={onConnectFb} disabled={connecting} data-testid="reconnect-freshbooks">
                  <ExternalLink size={12} /> {connecting ? "redirecting…" : "reconnect"}
                </button>
              )}
              <button type="button" className="btn-outline flex items-center gap-2"
                      onClick={onDisconnectFb} data-testid={`disconnect-${c.id}`}>
                <XCircle size={12} /> disconnect
              </button>
            </>
          ) : (
            <button type="button" className="btn-primary flex items-center gap-2"
                    onClick={onConnectFb} disabled={connecting}
                    data-testid="connect-freshbooks">
              <ExternalLink size={12} />
              {connecting ? "redirecting…" : "connect to freshbooks"}
            </button>
          )
        ) : isOdoo ? (
          c.connected ? (
            <button type="button" className="btn-outline flex items-center gap-2"
                    onClick={onDisconnectOdoo} data-testid={`disconnect-${c.id}`}>
              <XCircle size={12} /> disconnect
            </button>
          ) : (
            <button type="button" className="btn-primary flex items-center gap-2"
                    onClick={onConnectOdoo} data-testid="connect-odoo">
              <CheckCircle2 size={12} /> connect to odoo
            </button>
          )
        ) : c.erp === "netsuite" ? (
          <>
            <NetSuiteNotifyButton />
            <HelpTip text="Coming soon. Click Notify me when live and we'll email you when it's ready."
                     anchor="netsuite" label="About NetSuite" testid="help-netsuite" />
          </>
        ) : (
          <button type="button"
                  className={c.connected ? "btn-outline flex items-center gap-2" : "btn-primary flex items-center gap-2"}
                  onClick={() => onToggleMock(c.id, c.connected)}
                  data-testid={`toggle-${c.id}`}>
            {c.connected ? <><XCircle size={12} /> disconnect</> : <><CheckCircle2 size={12} /> connect</>}
          </button>
        )}
        {(isOauth || isOdoo) && c.connected && (
          <HelpTip text="Stops syncing right away. Your LF history is kept. See Help."
                   anchor="disconnecting" label="About disconnecting" testid={`help-disconnect-${c.erp}`} />
        )}
        <div className="label-mini">
          {c.erp === "netsuite"
            ? "coming soon — click notify me and we'll email you when live"
            : isOdoo
            ? (c.connected ? (c.last_error || "real invoices pulled every 5 min")
                           : "click connect and enter your odoo url, database, login and api key")
            : isOauth
            ? (c.connected ? "real invoices pulled every 5 min"
                           : `click connect to authorize via ${isQbo ? "intuit" : isXero ? "xero" : isZoho ? "zoho" : isFb ? "freshbooks" : "microsoft entra"}`)
            : (c.connected ? "actively generating simulated invoices" : "scheduler skips this endpoint")}
        </div>
      </div>
      </>)}
    </div>
  );
}


function NetSuiteNotifyButton() {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  // Fetch once on mount — if the user has already clicked, show the
  // confirmed state instead of the primary CTA.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/netsuite/interest");
        if (r.data?.notified) setState("done");
      } catch (_e) { /* silent */ }
    })();
  }, []);
  const notify = async () => {
    setState("loading");
    try {
      await api.post("/netsuite/interest", {});
      setState("done");
    } catch (_e) {
      setState("error");
    }
  };
  if (state === "done") {
    return (
      <div
        className="text-xs uppercase tracking-widest border border-emerald-500 bg-emerald-50 text-emerald-900 px-3 py-1.5 flex items-center gap-2"
        data-testid="netsuite-interest-confirmed"
      >
        ✓ we'll let you know
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={notify}
      disabled={state === "loading"}
      className="btn-primary flex items-center gap-2"
      data-testid="netsuite-notify-me"
    >
      {state === "loading" ? "saving…" : state === "error" ? "retry" : "notify me when live"}
    </button>
  );
}
